import type { EnumObjectToUnion } from "@/utils/enum";

export const EnvMode = {
  production: "production",
  staging: "staging",
  demo: "demo",
  development: "development",
  local: "local",
} as const;
export type EnvMode = EnumObjectToUnion<typeof EnvMode>;

// NOTE:誤字を防ぐためユニオン型で定義
type Features = "NewFeature" | "Starlink" | "ScheduleTransfer" | "MdmPreview";

const featureFlagStatus = {
  on: "on",
  off: "off",
  only: "only",
} as const;

type Flag =
  | { flag: typeof featureFlagStatus.on | typeof featureFlagStatus.off }
  | { flag: typeof featureFlagStatus.only; organizationIds: string[] };

type EnvFlag = Record<EnvMode, Flag>;

const FlagDefinitions: Record<Features, EnvFlag> = {
  NewFeature: {
    local: { flag: featureFlagStatus.on },
    development: { flag: featureFlagStatus.on },
    staging: {
      flag: featureFlagStatus.only,
      organizationIds: [
        // STAND&FORCE株式会社
        "ONN2tqLS4zx",
        // [開発用] DressCode 開発
        "O_zdc-dev-team",
        // [STG]株式会社QA カスタマー①B
        "O_zdc-customer1b",
        // [STG]株式会社QA カスタマー②A
        "O_zdc-customer2a",
        // [STG]株式会社QA カスタマー③A
        "OCIL22wq6lK",
        // [STG]株式会社QA カスタマー③B
        "OCEjFF2N20S",
        // [STG]株式会社QA ベンダー①
        "O_zdc-vendor1",
        // [STG]株式会社QA ベンダー②
        "O_zdc-vendor2",
        // Dress Code Demo Inc.
        "O22ApQXC0Ks",
      ],
    },
    demo: {
      flag: featureFlagStatus.only,
      organizationIds: [
        // STAND&FORCE株式会社
        "ONN2tqLS4zx",
        // [開発用] DressCode 開発
        "O_zdc-dev-team",
        // [STG]株式会社QA カスタマー①B
        "O_zdc-customer1b",
        // [STG]株式会社QA カスタマー②A
        "O_zdc-customer2a",
        // [STG]株式会社QA カスタマー③A
        "OCIL22wq6lK",
        // [STG]株式会社QA カスタマー③B
        "OCEjFF2N20S",
        // [STG]株式会社QA ベンダー①
        "O_zdc-vendor1",
        // [STG]株式会社QA ベンダー②
        "O_zdc-vendor2",
        // Dress Code Demo Inc.
        "O22ApQXC0Ks",
        // 株式会社Dress Codeデモ
        "O3g9NxlZAi5",
      ],
    },
    production: { flag: featureFlagStatus.off },
  },
  Starlink: {
    local: { flag: featureFlagStatus.on },
    development: { flag: featureFlagStatus.on },
    staging: {
      flag: featureFlagStatus.only,
      organizationIds: [
        // STAND&FORCE株式会社
        "ONN2tqLS4zx",
        // 株式会社Dress Codeデモ
        "O3g9NxlZAi5",
      ],
    },
    demo: { flag: featureFlagStatus.off },
    production: {
      flag: featureFlagStatus.only,
      organizationIds: [
        // BKI
        "OSGRbbrLezu",
        // IIJ Indonesia
        "OujoCYsIez2",
      ],
    },
  },
  ScheduleTransfer: {
    local: { flag: featureFlagStatus.on },
    development: { flag: featureFlagStatus.on },
    staging: { flag: featureFlagStatus.on },
    demo: { flag: featureFlagStatus.on },
    production: { flag: featureFlagStatus.off },
  },
  MdmPreview: {
    local: { flag: featureFlagStatus.on },
    development: { flag: featureFlagStatus.on },
    staging: { flag: featureFlagStatus.on },
    demo: { flag: featureFlagStatus.on },
    production: { flag: featureFlagStatus.off },
  },
};

export function createFeatureFlags({
  getSession,
  getMode,
}: {
  getSession: () => { organizationId: string } | null;
  getMode: () => EnvMode;
}) {
  function featureFlagContext() {
    const session = getSession();
    if (!session) {
      // セッションが存在しない場合のフォールバック処理
      return { organizationId: "" };
    }
    return {
      organizationId: session.organizationId,
    };
  }

  /** 環境モードに一致するフラグを取得 */
  function matchEnv(envModeFlag: EnvFlag): Flag {
    const mode = getMode();
    const envModeValues = Object.values(EnvMode);
    if (!envModeValues.includes(mode)) {
      throw new Error(`Unknown environment mode: ${mode}`);
    }
    return envModeFlag[mode];
  }

  return {
    /**
     * 指定されたFeatureFlagが有効かを検証します
     * @param {Features} feature - チェックするフィーチャー
     * @returns {boolean} FeatureFlagが有効な場合はtrue、無効な場合はfalse
     */
    check(feature: Features): boolean {
      const { organizationId } = featureFlagContext();
      const flag = matchEnv(FlagDefinitions[feature]);

      if (flag.flag === featureFlagStatus.on) {
        return true;
      }
      if (flag.flag === featureFlagStatus.off) {
        return false;
      }
      if (flag.flag === featureFlagStatus.only) {
        return flag.organizationIds.includes(organizationId);
      }
      // フラグステータスが不明な場合のフォールバック（基本的に発生しない）
      return false;
    },
  };
}

export function getMode(): EnvMode {
  /**
   * 環境モードを取得するロジック
   * @note 型推論が効かないため明示的にtype assertionを行う
   * @see https://ja.vite.dev/guide/env-and-mode#env-variables
   */
  return import.meta.env.MODE as EnvMode;
}
